import { useState } from "react";
import { supabase } from "../supabaseClient";
import { useLanguage } from "../i18n/LanguageContext";

export function usePracticeSubjects() {
  const { language } = useLanguage();

  const [practiceDomains, setPracticeDomains] =
    useState([]);

  const [practiceTopics, setPracticeTopics] =
    useState([]);

  const [loadingPracticeSubjects, setLoadingPracticeSubjects] =
    useState(false);

  const fetchPracticeDomains = async () => {
    setLoadingPracticeSubjects(true);

    const { data, error } = await supabase
      .from("question_domains")
      .select(
        `
        id,
        domain_code,
        domain_name,
        description,
        display_order
        `
      )
      .eq("status", "active")
      .order("display_order", {
        ascending: true,
      })
      .order("domain_name", {
        ascending: true,
      });

    if (error) {
      console.error(
        "FETCH PRACTICE DOMAINS ERROR:",
        error
      );

      setPracticeDomains([]);
      setLoadingPracticeSubjects(false);

      return [];
    }

    let localizedDomains = data || [];

    if (language !== "en" && localizedDomains.length > 0) {
      const domainIds = localizedDomains.map((domain) => domain.id);

      const { data: translations, error: translationError } =
        await supabase
          .from("question_taxonomy_translations")
          .select("entity_id, translated_name")
          .eq("entity_type", "domain")
          .eq("language_code", language)
          .in("entity_id", domainIds);

      if (translationError) {
        console.error(
          "FETCH DOMAIN TRANSLATIONS ERROR:",
          translationError
        );
      } else {
        const translationMap = new Map(
          (translations || []).map((item) => [
            item.entity_id,
            item.translated_name,
          ])
        );

        localizedDomains = localizedDomains.map((domain) => ({
          ...domain,
          domain_name:
            translationMap.get(domain.id) || domain.domain_name,
        }));
      }
    }

    setPracticeDomains(localizedDomains);
    setLoadingPracticeSubjects(false);

    return localizedDomains;
  };

  const fetchPracticeTopics = async (domainId) => {
    if (!domainId) {
      setPracticeTopics([]);
      return [];
    }

    setLoadingPracticeSubjects(true);

    const { data, error } = await supabase
      .from("question_topics")
      .select(
        `
        id,
        domain_id,
        topic_code,
        topic_name,
        description,
        display_order
        `
      )
      .eq("domain_id", domainId)
      .eq("status", "active")
      .order("display_order", {
        ascending: true,
      })
      .order("topic_name", {
        ascending: true,
      });

    if (error) {
      console.error(
        "FETCH PRACTICE TOPICS ERROR:",
        error
      );

      setPracticeTopics([]);
      setLoadingPracticeSubjects(false);

      return [];
    }

    let localizedTopics = data || [];

    if (language !== "en" && localizedTopics.length > 0) {
      const topicIds = localizedTopics.map((topic) => topic.id);

      const { data: translations, error: translationError } =
        await supabase
          .from("question_taxonomy_translations")
          .select("entity_id, translated_name")
          .eq("entity_type", "topic")
          .eq("language_code", language)
          .in("entity_id", topicIds);

      if (translationError) {
        console.error(
          "FETCH TOPIC TRANSLATIONS ERROR:",
          translationError
        );
      } else {
        const translationMap = new Map(
          (translations || []).map((item) => [
            item.entity_id,
            item.translated_name,
          ])
        );

        localizedTopics = localizedTopics.map((topic) => ({
          ...topic,
          topic_name:
            translationMap.get(topic.id) || topic.topic_name,
        }));
      }
    }

    setPracticeTopics(localizedTopics);
    setLoadingPracticeSubjects(false);

    return localizedTopics;
  };

  const clearPracticeTopics = () => {
    setPracticeTopics([]);
  };

  return {
    practiceDomains,
    practiceTopics,
    loadingPracticeSubjects,

    fetchPracticeDomains,
    fetchPracticeTopics,
    clearPracticeTopics,
  };
}